/**
 * Branch Context — global Branch filter, Super Admin only.
 *
 * Per-entity branch memory: the user's last-selected branch is remembered
 * SEPARATELY for AU, US, and Consolidated, because different entities have
 * different branches and the user wants to switch country without losing
 * their per-country filter preference.
 *
 *   localStorage keys
 *     branchFilter_AU    — last branch chosen while viewing AU   (default 'ALT')
 *     branchFilter_US    — last branch chosen while viewing US   (default 'all')
 *     branchFilter_all   — last branch chosen while Consolidated (default 'all')
 *     branchFilter       — mirror of the current entity's branch (kept in sync
 *                          so the legacy `getCurrentBranch()` helper used by
 *                          API service classes keeps working without changes)
 *
 * Non-Super-Admins never see the selector, and the backend forces a
 * sensible default (ALT for AU, 'all' for non-AU) regardless of what the
 * client sends — see `feedback_hardcoded_branch_alt` memory.
 *
 * Use 'all' as the value to disable branch filtering server-side.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useBusinessEntity } from './BusinessEntityContext';

const BranchContext = createContext();

export const useBranch = () => {
  const ctx = useContext(BranchContext);
  if (!ctx) throw new Error('useBranch must be used within a BranchProvider');
  return ctx;
};

const LEGACY_KEY = 'branchFilter';                 // mirrors current entity's branch
const KEY_FOR = (entity) => `branchFilter_${entity || 'all'}`;

// Per-entity defaults when no saved value exists for that entity yet.
//   AU → 'ALT'  (historical single-tenant default; matches what was in
//                MYOB ALT-AU when the system was AU-only)
//   US → 'all'  (no obvious "main" branch in the US set: AL/MS/TX/CORP)
//   all → 'all' (Consolidated is across-branch by definition)
const DEFAULT_FOR = (entity) => {
  if (entity === 'AU') return 'ALT';
  return 'all';
};

// One-shot migration from the pre-per-entity world: any saved value under
// the legacy single key `branchFilter` is carried into `branchFilter_AU`
// since the system was AU-only when that value was stored. Idempotent —
// only runs if branchFilter_AU isn't already set.
const migrateLegacyOnce = () => {
  try {
    const legacy = localStorage.getItem(LEGACY_KEY);
    const auSaved = localStorage.getItem(KEY_FOR('AU'));
    if (legacy && !auSaved) {
      localStorage.setItem(KEY_FOR('AU'), legacy);
    }
  } catch { /* localStorage unavailable — fine, defaults will apply */ }
};

// Warehouse stores branch values CHAR-padded ('ALT       '). Always trim on
// the client side so we send clean values to the backend (which compares with
// TRIM() on the SQL side) and so the dropdown renders without padding.
// Returns null for empty input — caller decides the default based on entity.
const norm = (v) => (v || '').toString().trim() || null;

// Read the saved branch for an entity, falling back to the entity's default.
// Encapsulates the migration + default logic so callers don't repeat it.
const readForEntity = (entity) => {
  try {
    return norm(localStorage.getItem(KEY_FOR(entity))) || DEFAULT_FOR(entity);
  } catch {
    return DEFAULT_FOR(entity);
  }
};

// Write the branch for an entity AND mirror to legacy key (for API services).
const writeForEntity = (entity, value) => {
  try {
    localStorage.setItem(KEY_FOR(entity), value);
    localStorage.setItem(LEGACY_KEY, value);
  } catch { /* ignore */ }
};

export const BranchProvider = ({ children }) => {
  const { hasRole, isAuthenticated } = useAuth();
  const { entity } = useBusinessEntity();
  const isSuperAdmin = isAuthenticated && hasRole && hasRole('Super Admin');

  const [branch, setBranchState] = useState(() => {
    migrateLegacyOnce();
    // Read the current entity's saved branch on first mount. entity is read
    // from localStorage by BusinessEntityContext, also in initial state, so
    // we can read it the same way here.
    let currentEntity = 'all';
    try { currentEntity = localStorage.getItem('businessEntity') || 'all'; }
    catch { /* fallback to 'all' */ }
    const initial = readForEntity(currentEntity);
    // Mirror to legacy key so getCurrentBranch() helpers see the right value
    // before the first setBranch call.
    try { localStorage.setItem(LEGACY_KEY, initial); } catch { /* ignore */ }
    return initial;
  });
  const [branches, setBranches] = useState([]);     // [{ branch, row_count }]
  const [branchesLoaded, setBranchesLoaded] = useState(false);

  // When the entity selector changes, restore that entity's saved branch.
  // Distinct effect from the branch-fetch one below so the state update
  // happens immediately (no waiting on the /branches network round-trip).
  useEffect(() => {
    if (!entity) return;
    const restored = readForEntity(entity);
    setBranchState(restored);
    try { localStorage.setItem(LEGACY_KEY, restored); } catch { /* ignore */ }
  }, [entity]);

  // Load distinct branch values once Super Admin authenticates. Backend returns
  // an empty list for non-Super-Admins, so this is a cheap no-op for them.
  // Also re-fetches whenever the global entity selector changes — different
  // entities have different branches (AU: ALT, AGI, ELEV; US: AL, MS, TX, CORP).
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isSuperAdmin) { setBranches([]); setBranchesLoaded(true); return; }
    let alive = true;
    const token = sessionStorage.getItem('oidc_token') || localStorage.getItem('token');
    const url = entity && entity !== 'all'
      ? `/api/finance/branches?entity=${encodeURIComponent(entity)}`
      : '/api/finance/branches';
    fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : { branches: [] })
      .then(data => {
        if (!alive) return;
        const list = Array.isArray(data?.branches) ? data.branches : [];
        const normalised = list
          .map(b => ({ ...b, branch: (b.branch || '').toString().trim() }))
          .filter(b => b.branch);
        setBranches(normalised);

        // Defensive: if the restored per-entity branch isn't actually present
        // in this entity's branch list (e.g. user previously selected a branch
        // that was later renamed/removed), fall back to 'all'.
        const current = readForEntity(entity || 'all');
        const validBranches = new Set(normalised.map(b => b.branch));
        if (current !== 'all' && validBranches.size > 0 && !validBranches.has(current)) {
          setBranchState('all');
          writeForEntity(entity || 'all', 'all');
        }
      })
      .catch(() => { /* silent — selector renders empty */ })
      .finally(() => alive && setBranchesLoaded(true));
    return () => { alive = false; };
  }, [isAuthenticated, isSuperAdmin, entity]);

  const setBranch = useCallback((value) => {
    const v = (value || '').toString().trim() || 'all';
    setBranchState(v);
    writeForEntity(entity || 'all', v);
  }, [entity]);

  const value = {
    branch,
    setBranch,
    branches,
    branchesLoaded,
    isSuperAdmin: !!isSuperAdmin,
  };

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
};

// Helper for API services — read directly from localStorage so they can attach
// the X-Branch header without going through React context. Reads the legacy
// mirror key (kept in sync with whichever entity is currently active).
// Always returns a trimmed value (warehouse stores CHAR-padded branches; we
// never want to send the padded form over HTTP).
export const getCurrentBranch = () => {
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    return (raw || '').trim() || 'all';
  } catch {
    return 'all';
  }
};
